import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatStep, MatStepLabel, MatStepper } from '@angular/material/stepper';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators, ValueChangeEvent } from '@angular/forms';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { MatLabel } from '@angular/material/form-field';
import { MatProgressBar } from '@angular/material/progress-bar';
import { ToastService } from '../../../../../shared/services/toast.service';
import { RoutesService } from '../../../../../data/routes/routes.service';
import {
  FieldFormat, GenerateTokenClient,
  LayerBasicModel,
  RouteConfiguration, RouteConfigurationAuth,
  RouteConfigurationWithSchema,
  RouteHierarchyItem,
  RouteImportAttribute,
  RouteSchema,
  RouteSourceType,
  TaskStatus
} from '../../../../../shared/models/route';
import { BehaviorSubject, firstValueFrom, interval, mergeMap, Subscription } from 'rxjs';
import { AsyncPipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { EmptyMapContent } from '../../../../../shared/components/map-preview/model/EmptyMapContent';
import { RouteConfigDetailMapContent } from '../../../../../shared/components/map-preview/model/map-content/RouteConfigDetailMapContent';
import { McRouteConfiguration } from '../../../../../shared/components/map-preview/model/McRouteConfiguration';
import { MapContent } from '../../../../../shared/components/map-preview/model/MapContent';
import { RouteFilter, RouteTreeComponent } from '../route-tree/route-tree.component';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatCheckbox } from '@angular/material/checkbox';
import { filter } from 'rxjs/operators';
import { FeatureCollection, Geometry } from 'geojson';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfigurationService } from '../../../../../configuration/configuration.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LineStyleEditorComponent } from '../../../../../shared/components/line-style-editor/line-style-editor.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RootRoute, SettingsRoute } from 'src/app/shared/models/angular-routing';
import shp from 'shpjs';
import { MapPreviewComponent } from '../../../../../shared/components/map-preview/map-preview.component';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ConfirmationDialogComponent } from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { LineStyle } from '../../../../../shared/models/styling';

const DEFAULT_ROUTE_COLOR = '#0059CC';
const HOVERED_ROUTE_COLOR = '#FBBC05';

const DEFAULT_CONFIG_NAME = 'All Routes';

const DEFAULT_ROUTE_STYLE = new LineStyle(DEFAULT_ROUTE_COLOR, 4, 1.0);

enum RoutePresentationStyle {
  SINGLE = 'single',
  ATTRIBUTE = 'attribute',
  INDIVIDUAL = 'individual',
}

class RouteEditorWizard {
  // input
  routeConfigurationId?: number;

  loadingData = new BehaviorSubject(false);

  /******
   * step 1 - Data Source
   ******/
  dataSourceType?: RouteSourceType;
  serverUrl = '';
  auth?: RouteConfigurationAuth;
  /******
   * step 2 - Layer And Route Name Attribute
   ******/
  layers: LayerBasicModel[] | undefined;
  uploadedFeatureCollection: FeatureCollection | undefined;
  loadingLayers = new BehaviorSubject(false);
  layerLoadingError?: string;
  fields: FieldFormat[];
  loadingFields = new BehaviorSubject(false);
  fieldsLoadingError?: string;
  // state
  selectedLayerId: number | undefined;
  nameField: string | undefined;

  attributes: string[];
  /******
   * step 3 - Route Selection And Grouping
   ******/
  categories: string[] = [];
  subRouteAttribute: string | undefined;

  routeFilter: RouteFilter = {routeIds: [], all: true};
  routeConfiguration: RouteConfiguration;
  configurationWithSchema: RouteConfigurationWithSchema;
  loadingFeatureCollection = new BehaviorSubject(false);

  featureTaskUuid: string | undefined;
  featureCollectionLoadingError: string | undefined;
  /******
   * step 4 - Route Presentation
   ******/
  stylePresentationOptions = [
    { value: RoutePresentationStyle.SINGLE, label: 'Single Symbol'},
    { value: RoutePresentationStyle.ATTRIBUTE, label: 'By Attribute'},
    { value: RoutePresentationStyle.INDIVIDUAL, label: 'Individual Route'},
  ];
  stylePresentation = RoutePresentationStyle.SINGLE;
  defaultRouteStyle = {...DEFAULT_ROUTE_STYLE};

  // attributes: string[] = [];
  /******
   * step 5 - Route Configuration Settings
   ******/
  configurationName: string = DEFAULT_CONFIG_NAME;
  keepUpToDate = false;
  exportToCustomMapLayers = false;
  updateNote: string | undefined;
  showUpdateNote = true;

  savingData = new BehaviorSubject(false);

  constructor(private readonly routesService: RoutesService,
              private readonly configurationService: ConfigurationService,
  ) {
  }

  hasDataForStep1() {
    return (this.dataSourceType === RouteSourceType.ARCGIS_SERVICE && this.layers) ||
      (this.dataSourceType === RouteSourceType.SHAPE_FILE && (this.uploadedFeatureCollection || this.configurationWithSchema));
  }

  async loadLayers() {
    // step 0
    delete this.layerLoadingError;
    // step 1
    delete this.layers;
    delete this.selectedLayerId;
    delete this.nameField;

    const {serverUrl, auth} = this;

    this.loadingLayers.next(true);
    try {
      const response = await this.routesService.getFeatureServiceDescription(
        serverUrl, auth,
      );
      this.layers = response.data.layers;
    } catch (error: any) {
      this.layerLoadingError = error;
      throw error;
    } finally {
      this.loadingLayers.next(false);
    }
  }

  async loadLayerDescription() {
    delete this.fields;
    delete this.nameField;

    delete this.routeConfiguration;
    delete this.configurationWithSchema;
    delete this.fieldsLoadingError;

    const {serverUrl, auth} = this;
    this.loadingFields.next(true);

    try {
      const response = await this.routesService.getFeatureServiceLayerDescription(
        serverUrl, this.selectedLayerId, auth,
      );
      this.fields = response.data.fields;
    } catch (error: any) {
      this.fieldsLoadingError = error;
      delete this.selectedLayerId;
      throw error;
    } finally {
      this.loadingFields.next(false);
    }
  }

  async loadShapeFile(shapeFile: File) {
    // step 0
    delete this.layerLoadingError;
    this.loadingLayers.next(true);
    try {
      const buffer = await shapeFile.arrayBuffer();
      const geojson = await shp(buffer);
      if (geojson.type !== 'FeatureCollection') {
        throw new Error('Unsupported geojson type');
      }
      if ((geojson as FeatureCollection).features.length === 0) {
        throw new Error('Empty feature collection');
      }
      if ((geojson as FeatureCollection).features[0].properties.length === 0) {
        throw new Error('Empty feature properties');
      }
      this.uploadedFeatureCollection = this.shrinkFeatureCollection(geojson as FeatureCollection);
      this.fields = Object.entries((this.uploadedFeatureCollection as FeatureCollection).features[0].properties)
        .map(([key, value]) => new FieldFormat(key, typeof value));
      this.selectedLayerId = 0;
    } catch (error: any) {
      this.layerLoadingError = error;
      throw error;
    } finally {
      this.loadingLayers.next(false);
    }
  }

  async loadFeatureCollection() {
    delete this.featureCollectionLoadingError;

    this.loadingFeatureCollection.next(true);
    this.updateAttributes();

    if (this.dataSourceType === RouteSourceType.ARCGIS_SERVICE) {
      let fields: string[];
      if (this.subRouteAttribute) {
        fields = [...this.attributes, this.subRouteAttribute];
      } else {
        fields = this.attributes;
      }
      // start async get feature config
      try {
        const response = await this.routesService.readFeatureCollectionFromFeatureProxyAsync(
          this.serverUrl,
          this.selectedLayerId,
          fields,
          this.auth,
        );
        // first clean any previous features data
        this.cleanFeatureTask();
        this.featureTaskUuid = response.data;
        console.log(`Task UUID: ${this.featureTaskUuid}`);
        const oldConfig = this.routeConfiguration;
        // repeatedly check the task status
        this.routeConfiguration = await this.getTaskResult(this.featureTaskUuid);
        // try to keep hidden route information
        const newConfig = this.routeConfiguration;
        if (oldConfig && // old config exists
          ((oldConfig.subRouteItems.length === 0 && newConfig.subRouteItems.length > 0) || // attribute-based styling changed
            (oldConfig.subRouteItems.length > 0 && newConfig.subRouteItems.length === 0))
        ) {
          // this is the special case where the config is reloaded because of sub-route attribute
          // route hierarchy won't change, and we need to keep 'hidden' information
          this.routeConfiguration.children = oldConfig.children;
        }
        this.updateRouteConfigurationWithSchemaFromWizard();

        return await this.loadFeatureCollectionGeoJson(response.data);
      } catch (error: any) {
        console.error(error);
        throw error;
      } finally {
        this.loadingFeatureCollection.next(false);
      }
    } else {
      // initial upload
      if (this.uploadedFeatureCollection) {
        try {
          const response = await this.routesService.uploadFeatureCollection(this.uploadedFeatureCollection);
          this.cleanFeatureTask();
          this.featureTaskUuid = response.data;
          this.routeConfiguration = await this.getTaskResult(this.featureTaskUuid);
          this.updateRouteConfigurationWithSchemaFromWizard();
          this.loadingFeatureCollection.next(false);
          return await this.loadFeatureCollectionGeoJson(response.data);
        } catch (error: any) {
          console.error(error);
          throw error;
        }
      } else {
        // open existing configuration
        this.updateRouteConfigurationWithSchemaFromWizard();
        this.loadingFeatureCollection.next(false);
        return await this.routesService.getRouteGeoJsonById(this.routeConfigurationId);
      }
    }
  }

  async getTaskResult(taskUuid: string) {
    return firstValueFrom(
      interval(2000).pipe(
        mergeMap(() => {
          console.log(`checking task ${taskUuid} status`);
          return this.taskStatusHandler(taskUuid);
        }),
        filter(status => {
          switch (status) {
            case TaskStatus.DONE:
              return true;
            case TaskStatus.FAILURE:
              throw new Error('Error loading task status');
            default:
              return false;
          }
        }),
        mergeMap(() => {
          return this.loadTaskResult(taskUuid);
        })
      )
    );
  }

  hasDataForStep2() {
    return (
        (this.dataSourceType === RouteSourceType.ARCGIS_SERVICE && this.selectedLayerId !== undefined)
        || this.dataSourceType === RouteSourceType.SHAPE_FILE
      )
      && this.nameField !== undefined
      && this.configurationWithSchema;
  }

  async addCategory(fieldName: string) {
    this.categories.push(fieldName);
    return await this.loadFeatureCollection();
  }

  async removeCategory(fieldName: string) {
    const index = this.categories.indexOf(fieldName);
    if (index === -1) {
      throw new Error('Cannot remove category');
    }
    this.categories.splice(index, 1);
    return await this.loadFeatureCollection();
  }

  async addSubRoute(fieldName: string) {
    this.subRouteAttribute = fieldName;
    return await this.loadFeatureCollection();
  }

  setRouteFilter(routeFilter: RouteFilter) {
    this.routeFilter = routeFilter;
  }

  setRouteConfiguration(routeConfiguration: RouteConfiguration) {
    this.routeConfiguration = routeConfiguration;
    this.updateRouteConfigurationWithSchemaFromWizard();
  }

  updateRouteConfigurationWithSchemaFromWizard() {
    const routeConfig = new RouteConfiguration();
    routeConfig.childrenAttribute = this.nameField;
    routeConfig.children = this.routeConfiguration.children;
    if (this.routeConfiguration.subRouteItems && this.routeConfiguration.subRouteItems.length > 0
      && !this.routeConfiguration.subRouteItems[0].style
    ) {
      // if style not initialized, set default values
      this.routeConfiguration.subRouteItems.map(item => {
        item.style = {...DEFAULT_ROUTE_STYLE};
      });
    }
    routeConfig.subRouteItems = this.routeConfiguration.subRouteItems;
    this.configurationWithSchema = new RouteConfigurationWithSchema(
      this.configurationName,
      this.dataSourceType,
      this.serverUrl,
      this.auth,
      this.selectedLayerId,
      this.layers?.find(layer => layer.id === this.selectedLayerId).name,
      this.keepUpToDate,
      this.exportToCustomMapLayers,
      new RouteSchema(
        this.attributes.map(attribute => {
          return new RouteImportAttribute(attribute);
        }),
        this.subRouteAttribute ? new RouteImportAttribute(this.subRouteAttribute) : undefined,
        this.defaultRouteStyle,
      ),
      routeConfig,
    );
    this.configurationWithSchema.updateNote = '';
    this.configurationWithSchema.id = this.routeConfigurationId;
  }

  async saveChanges() {
    const {configurationWithSchema, configurationName, keepUpToDate, exportToCustomMapLayers, updateNote} = this;
    configurationWithSchema.name = configurationName;
    configurationWithSchema.regularUpdate = keepUpToDate;
    configurationWithSchema.updateCustomLayers = exportToCustomMapLayers;
    configurationWithSchema.schema.style = this.defaultRouteStyle;
    configurationWithSchema.updateNote = updateNote;

    this.savingData.next(true);

    console.log('Saving route configuration...');
    try {
      const response = await this.routesService.updateRouteConfiguration(configurationWithSchema);
      console.log('Route configuration saved.');

      // if imported from shape file, we cannot save geometries
      if (this.featureTaskUuid) {
        console.log('Saving route geometries...');
        await this.routesService.updateRouteGeoJson(this.featureTaskUuid, response.data.id, this.configurationWithSchema);
        console.log('Route geometries saved.');

        this.cleanFeatureTask();
      }

      if (exportToCustomMapLayers) {
        await this.configurationService.updateRouteMapLayer(response.data.id);
        console.log('Custom map layer saved.');
        this.configurationService.refreshConfiguration();
      }
    } catch (error: any) {
      throw error;
    } finally {
      this.savingData.next(false);
    }
  }

  async deleteRouteConfiguration() {
    if (this.routeConfigurationId === undefined) {
      return Promise.reject('cannot delete not-saved route configuration');
    }
    this.loadingData.next(true);
    const result = await this.routesService.deleteRouteConfiguration(this.routeConfigurationId);
    this.loadingData.next(false);
    return result;
  }

  creatingNewRouteConfiguration() {
    delete this.routeConfigurationId;
    delete this.serverUrl;
    delete this.auth;

    delete this.layers;
    delete this.layerLoadingError;
    delete this.selectedLayerId;
    delete this.nameField;
    delete this.fieldsLoadingError;

    this.categories = [];
    this.routeFilter = {routeIds: [], all: false};
    delete this.routeConfiguration;
    delete this.configurationWithSchema;

    delete this.featureTaskUuid;
    delete this.featureCollectionLoadingError;

    this.defaultRouteStyle = new LineStyle(DEFAULT_ROUTE_COLOR, 4, 1.0);

    this.configurationName = DEFAULT_CONFIG_NAME;
    this.keepUpToDate = false;
    this.exportToCustomMapLayers = false;
    this.updateNote = undefined;
  }

  async loadRouteConfiguration(configurationId: number) {
    this.loadingData.next(true);
    try {
      const {data, data: {schema}} = await this.routesService.getRouteConfiguration(configurationId);

      this.configurationName = data.name;
      this.serverUrl = data.url;
      this.auth = data.auth;
      this.dataSourceType = data.source;

      if (this.dataSourceType === RouteSourceType.ARCGIS_SERVICE) {
        await this.loadLayers();
        this.selectedLayerId = data.layerId;
        await this.loadLayerDescription();
      } else if (this.dataSourceType === RouteSourceType.SHAPE_FILE) {
        this.selectedLayerId = 0;
      }

      this.nameField = schema.classification[schema.classification.length - 1].attribute;
      this.attributes = schema.classification.map(att => att.attribute);
      this.categories = this.attributes.splice(0, this.attributes.length - 1);
      this.subRouteAttribute = schema.subRouteAttribute?.attribute;
      this.stylePresentation = this.subRouteAttribute
        ? RoutePresentationStyle.ATTRIBUTE
        : (RouteHierarchyItem.getLeavesWithExplicitColors(
          schema.classification.length,
          data.configuration.children,
          ).filter(i => i.styleOverride).length > 0
            ? RoutePresentationStyle.INDIVIDUAL
            : RoutePresentationStyle.SINGLE
        );

      this.defaultRouteStyle = schema.style;
      this.routeConfiguration = data.configuration;
      this.routeConfigurationId = data.id;
      this.keepUpToDate = data.regularUpdate;
      this.exportToCustomMapLayers = data.updateCustomLayers;
      this.showUpdateNote = false;

      return await this.loadFeatureCollection();
    } catch (_) {
    } finally {
      this.loadingData.next(false);
    }
    return null;
  }

  private cleanFeatureTask() {
    if (this.featureTaskUuid) {
      this.routesService.cleanFeatureTaskStatus(this.featureTaskUuid).catch();
      this.featureTaskUuid = null;
    }
  }

  private async taskStatusHandler(taskUuid: string) {
    console.log('Checking task status...');
    return await this.routesService.getFeatureTaskStatus(taskUuid).then(taskStatus => {
      return taskStatus.data;
    });
  }

  private async loadTaskResult(taskUuid: string) {
    try {
      const response = await this.routesService.getRouteConfigurationFromFeatureProxy(
        taskUuid,
        this.routeConfigurationId,
        this.serverUrl,
        this.selectedLayerId,
        this.attributes,
        this.subRouteAttribute,
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw error;
    }
  }

  // load the feature collection with all necessary properties
  private async loadFeatureCollectionGeoJson(taskUuid: string) {
    return this.routesService.getRoutesGeoJsonFromExternalService(
      taskUuid,
      this.configurationWithSchema.layerId,
      this.attributes,
      this.subRouteAttribute,
      this.configurationWithSchema.configuration,
    ).then(response => response.data);
  }

  private updateAttributes() {
    this.attributes = [...this.categories, this.nameField];
  }

  defaultRouteStyleChanged(routeStyle: LineStyle) {
    this.defaultRouteStyle = routeStyle;
  }

  step1SourceDataChanged() {
    if (!this.layers) {
      return false;
    }
    delete this.layers;
    delete this.layerLoadingError;
    delete this.selectedLayerId;
    delete this.nameField;
    delete this.fieldsLoadingError;

    this.categories = [];
    delete this.subRouteAttribute;
    this.routeFilter = {routeIds: [], all: false};
    delete this.routeConfiguration;
    delete this.configurationWithSchema;

    delete this.featureTaskUuid;
    delete this.featureCollectionLoadingError;
    return true;
  }

  nameFieldChanged() {
    delete this.routeConfiguration;
    delete this.configurationWithSchema;
    delete this.featureTaskUuid;
    delete this.featureCollectionLoadingError;
  }

  roundCoordinates(coords: number[]): number[] {
    // round to 6 decimals
    return coords.map(c => Math.round(c * 1e6) / 1e6);
  }

  truncateCoordinates(geometry: Geometry): Geometry {
    switch (geometry.type) {
      case 'Point':
        return { ...geometry, coordinates: this.roundCoordinates(geometry.coordinates) };

      case 'MultiPoint':
      case 'LineString':
        return { ...geometry, bbox: undefined, coordinates: geometry.coordinates.map(this.roundCoordinates) };

      case 'MultiLineString':
      case 'Polygon':
        return { ...geometry, bbox: undefined, coordinates: geometry.coordinates.map(ring => ring.map(this.roundCoordinates)) };

      case 'MultiPolygon':
        return { ...geometry, coordinates: geometry.coordinates.map(poly => poly.map(ring => ring.map(this.roundCoordinates))) };

      case 'GeometryCollection':
        return { ...geometry, geometries: geometry.geometries.map(this.truncateCoordinates) };

      default:
        return geometry;
    }
  }

  shrinkFeatureCollection(fc: FeatureCollection): FeatureCollection {
    return {
      ...fc,
      features: fc.features.map(feature => ({
        ...feature,
        geometry: feature.geometry ? this.truncateCoordinates(feature.geometry) : feature.geometry,
      })),
    };
  }
}

@Component({
  selector: 'app-route-configuration-editor',
  imports: [
    MatStepper,
    MatStep,
    MatStepLabel,
    MatButton,
    ReactiveFormsModule,
    MatInput,
    MatLabel,
    MatFormField,
    MatProgressBar,
    MatOption,
    MatSelect,
    FormsModule,
    RouteTreeComponent,
    MatIcon,
    MatIconButton,
    RouterLink,
    MatCheckbox,
    MatTooltip,
    MatProgressSpinner,
    LineStyleEditorComponent,
    MatError,
    MapPreviewComponent,
    AsyncPipe,
    NgStyle,
    NgTemplateOutlet,
    CdkTextareaAutosize,
  ],
  templateUrl: './route-configuration-editor.component.html',
  styleUrls: ['./route-configuration-editor.component.scss', '../../../settings-common.scss'],
})
export class RouteConfigurationEditorComponent implements OnDestroy, OnInit {
  wizard: RouteEditorWizard;

  @ViewChild('stepper')
  stepper: MatStepper;

  // step 0
  dataSourceFormGroup: FormGroup;

  mapContent: MapContent = EmptyMapContent.instance;

  // step 4
  routeConfigurationSettingsFormGroup: FormGroup;

  subscriptions: Subscription[] = [];
  currentStep = -1;
  groupBeingAdded: any;
  subRouteBeingAdded: string = null;

  readonly RouteSourceType = RouteSourceType;
  readonly RootRoute = RootRoute;
  readonly SettingsRoute = SettingsRoute;
  readonly GenerateTokenClient = GenerateTokenClient;
  readonly DEFAULT_ROUTE_STYLE = DEFAULT_ROUTE_STYLE;
  readonly RoutePresentationStyle = RoutePresentationStyle;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly routesService: RoutesService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly configurationService: ConfigurationService,
    private readonly toastService: ToastService
  ) {
    this.createWizard();
    this.createFormGroups();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.activatedRoute.params.subscribe(params => {
        const configurationId = params.configurationId;
        if (!isNaN(+configurationId)) {
          this.processMapContext(this.wizard.loadRouteConfiguration(Number(configurationId)))
            .then(() => {
              const { dataSourceType, serverUrl, auth, configurationName, keepUpToDate, exportToCustomMapLayers, subRouteAttribute } =
                this.wizard;
              this.dataSourceFormGroup.setValue({
                serverUrl,
                useCredentials: !!auth,
                username: auth?.username ? auth.username : null,
                password: auth?.password ? auth.password : null,
                generateTokenClient: auth?.client ? auth.client : null,
              });
              this.subRouteBeingAdded = subRouteAttribute;
              let stepsToForward = 0;
              this.currentStep = 0;

              if (this.wizard.hasDataForStep1()) {
                stepsToForward += 4;
                this.currentStep = 4;
                this.routeConfigurationSettingsFormGroup.setValue({
                  configurationName,
                  keepUpToDate,
                  exportToCustomMapLayers,
                  updateNote: '',
                });
                if (dataSourceType === RouteSourceType.SHAPE_FILE) {
                  this.routeConfigurationSettingsFormGroup.get('keepUpToDate').disable();
                }
              }

              setTimeout(() => {
                while (stepsToForward--) {
                  this.stepper.next();
                }
              }, 10);
            })
            .catch(e => console.log(e));
        } else {
          this.wizard.creatingNewRouteConfiguration();
          this.currentStep = 0;
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  step1LoadLayers() {
    this.dataSourceFormGroup.markAsPristine();
    this.wizard
      .loadLayers()
      .then(() => {
        this.routeConfigurationSettingsFormGroup.get('keepUpToDate').enable();
        setTimeout(() => this.stepper.next(), 100);
      })
      .catch(error => {
        this.toastService.long('Failed to get Feature Service Description! Error: ' + error);
      });
  }

  step1LoadShapeFile(event: Event) {
    const selectedFile = (event.target['files'] as FileList)[0];
    if (selectedFile) {
      this.wizard
        .loadShapeFile(selectedFile)
        .then(() => {
          this.routeConfigurationSettingsFormGroup.get('keepUpToDate').disable();
          setTimeout(() => this.stepper.next(), 100);
        })
        .catch(error => {
          this.toastService.long('Error on opening shape file! Error: ' + error);
        });
    }
  }

  step2SelectedLayerChanged() {
    this.wizard.loadLayerDescription().catch(error => this.toastService.long(error));
  }

  step1NameFieldChanged() {
    this.wizard.nameFieldChanged();
  }

  step1LoadRoutes() {
    this.processMapContext(this.wizard.loadFeatureCollection())
      .then(() => {
        setTimeout(() => {
          this.stepper.next();
        }, 100);
      })
      .catch(error => {
        this.toastService.long(error);
      });
  }

  filterRoutes(routeFilter: RouteFilter) {
    this.wizard.setRouteFilter(routeFilter);
    this.updateMapRouteVisibility();
  }

  colorsOverrideChanged($event: RouteConfiguration) {
    this.wizard.setRouteConfiguration($event);
    this.updateMapCustomStyleOverrides();
  }

  onStepperSelectionChange(event: StepperSelectionEvent) {
    const { selectedIndex } = event;
    this.currentStep = selectedIndex;
    switch (this.currentStep) {
      case 2:
        this.updateMapConfigurationStyle(DEFAULT_ROUTE_STYLE);
        break;
      case 3:
        this.updateMapConfigurationStyle(this.wizard.defaultRouteStyle);
        break;
    }
  }

  nextStep() {
    this.stepper.next();
  }

  onCategorySelected($event: MatSelectChange) {
    this.processMapContext(this.wizard.addCategory($event.value)).then(() => {
      this.groupBeingAdded = undefined;
    });
  }

  removeCategory(category: string) {
    this.processMapContext(this.wizard.removeCategory(category)).then();
  }

  stylePresentationChanged(stylePresentation: string) {
    if (stylePresentation === RoutePresentationStyle.SINGLE) {
      if (this.wizard.subRouteAttribute !== undefined) {
        this.subRouteBeingAdded = undefined;
        this.onSubRouteSelected();
      } else {
        // reset style overrides - changing from individual to single
        const routeItems = RouteHierarchyItem.getItemsNoOverrides(
          this.wizard.routeConfiguration.children,
          this.wizard.configurationWithSchema.id
        );
        this.wizard.routeConfiguration.children = routeItems;
        this.wizard.configurationWithSchema.configuration.children = routeItems;
        const styleReset = { ...DEFAULT_ROUTE_STYLE };
        this.updateMapConfigurationStyle(styleReset);
      }
    } else if (stylePresentation === RoutePresentationStyle.ATTRIBUTE) {
      const styleReset = { ...DEFAULT_ROUTE_STYLE };
      this.defaultRouteStyleChanged(styleReset);
    } else if (stylePresentation === RoutePresentationStyle.INDIVIDUAL) {
      const styleReset = { ...DEFAULT_ROUTE_STYLE };
      this.defaultRouteStyleChanged(styleReset);
      if (this.wizard.subRouteAttribute !== undefined) {
        this.subRouteBeingAdded = undefined;
        this.onSubRouteSelected();
      }
    }
  }

  onSubRouteSelected() {
    this.processMapContext(this.wizard.addSubRoute(this.subRouteBeingAdded)).then(() => {});
  }

  isExistingAndShapeUploaded() {
    return this.wizard.dataSourceType === RouteSourceType.SHAPE_FILE && !this.wizard.uploadedFeatureCollection;
  }

  onRouteHover(route: RouteHierarchyItem) {
    switch (this.currentStep) {
      case 2:
        route.styleOverride = { color: HOVERED_ROUTE_COLOR };
        this.updateMapConfigurationStyle(DEFAULT_ROUTE_STYLE);
        break;
      case 3:
        // this.updateMapCustomStyleOverrides();
        break;
    }
  }

  onRouteLeave(route: RouteHierarchyItem) {
    switch (this.currentStep) {
      case 2:
        delete route.styleOverride;
        this.updateMapConfigurationStyle(DEFAULT_ROUTE_STYLE);
        break;
      case 3:
        // this.updateMapCustomStyleOverrides();
        break;
    }
  }

  saveChanges() {
    if (this.wizard.showUpdateNote) {
      this.wizard
        .saveChanges()
        .then(() => {
          this.toastService.long('Configuration has been updated');
          this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_ROUTES]).then();
        })
        .catch(error => {
          this.toastService.long(error);
        });
    } else {
      this.wizard.showUpdateNote = true;
    }
  }

  createWizard() {
    this.wizard = new RouteEditorWizard(this.routesService, this.configurationService);
    this.subscriptions.push(
      this.wizard.loadingLayers.subscribe(loading => {
        if (loading) {
          this.dataSourceFormGroup.disable();
        } else {
          this.dataSourceFormGroup?.enable();
        }
      })
    );
  }

  updateMapRouteVisibility() {
    if (this.isMapContentUpdateAllowed()) {
      const castMapContent = this.mapContent as RouteConfigDetailMapContent;
      const { configurationWithSchema, defaultRouteStyle, routeFilter } = this.wizard;
      castMapContent.changeRouteVisibility(
        McRouteConfiguration.fromBaseAndStyle(configurationWithSchema, defaultRouteStyle, routeFilter, true)
      );
    }
  }

  updateMapConfigurationStyle(routeStyle: LineStyle) {
    if (this.isMapContentUpdateAllowed()) {
      const castMapContent = this.mapContent as RouteConfigDetailMapContent;
      const { configurationWithSchema, routeFilter } = this.wizard;
      castMapContent.updateLayerStyles(McRouteConfiguration.fromBaseAndStyle(configurationWithSchema, routeStyle, routeFilter, true));
    }
  }

  updateMapCustomStyleOverrides() {
    if (this.isMapContentUpdateAllowed()) {
      const castMapContent = this.mapContent as RouteConfigDetailMapContent;
      const { configurationWithSchema, defaultRouteStyle, routeFilter } = this.wizard;
      castMapContent.updateLayerStyles(
        McRouteConfiguration.fromBaseAndStyle(configurationWithSchema, defaultRouteStyle, routeFilter, true)
      );
    }
  }

  isMapContentUpdateAllowed() {
    return this.wizard.configurationWithSchema && this.mapContent && this.mapContent instanceof RouteConfigDetailMapContent;
  }

  createFormGroups() {
    const { serverUrl, auth } = this.wizard;
    this.dataSourceFormGroup = this.formBuilder.group({
      serverUrl: this.formBuilder.control(serverUrl, [Validators.required]),
      useCredentials: this.formBuilder.control(!!auth),
      username: this.formBuilder.control(auth?.username),
      password: this.formBuilder.control(auth?.password),
      generateTokenClient: this.formBuilder.control(auth?.client),
    });

    this.subscriptions.push(
      this.dataSourceFormGroup.events.subscribe(event => {
        if (this.currentStep !== 0) {
          return;
        }
        if (event instanceof ValueChangeEvent) {
          // tslint:disable-next-line:no-shadowed-variable
          const { serverUrl, useCredentials, username, password, generateTokenClient } = event.value;
          this.wizard.serverUrl = serverUrl;
          this.wizard.auth =
            useCredentials && username && password && generateTokenClient
              ? new RouteConfigurationAuth(username, password, generateTokenClient)
              : null;
        }
        if (this.dataSourceFormGroup.dirty) {
          // remove the loaded layers on any touched change
          if (this.wizard.step1SourceDataChanged()) {
            this.stepper.reset();
          }
        }
      })
    );

    this.routeConfigurationSettingsFormGroup = this.formBuilder.group({
      configurationName: this.formBuilder.control('', [Validators.required]),
      keepUpToDate: this.formBuilder.control(false),
      exportToCustomMapLayers: this.formBuilder.control(false),
      updateNote: this.formBuilder.control(''),
    });

    this.subscriptions.push(
      this.routeConfigurationSettingsFormGroup.events.subscribe(event => {
        if (this.currentStep !== 4) {
          return;
        }
        if (event instanceof ValueChangeEvent) {
          const { configurationName, keepUpToDate, exportToCustomMapLayers, updateNote } = event.value;
          this.wizard.configurationName = configurationName;
          this.wizard.keepUpToDate = keepUpToDate;
          this.wizard.exportToCustomMapLayers = exportToCustomMapLayers;
          this.wizard.updateNote = updateNote;
        }
      })
    );
  }

  async processMapContext(promise: Promise<FeatureCollection | void>) {
    try {
      const geometries = await promise;
      if (geometries) {
        this.updateMapContext(geometries);
      }
      return geometries;
    } catch (e) {
      this.resetMapContext();
      throw e;
    }
  }

  private updateMapContext(geometries: FeatureCollection) {
    this.mapContent = new RouteConfigDetailMapContent(
      McRouteConfiguration.fromBase(this.wizard.configurationWithSchema, this.wizard.routeFilter, true),
      geometries
    );
  }

  private resetMapContext() {
    this.mapContent = EmptyMapContent.instance;
  }

  defaultRouteStyleChanged(routeStyle: LineStyle) {
    this.wizard.defaultRouteStyleChanged(routeStyle);
    this.updateMapConfigurationStyle(routeStyle);
  }

  subRouteStyleChanged(subRouteValue: string, routeStyle: LineStyle) {
    this.updateMapConfigurationStyle(routeStyle);
  }

  deleteRouteConfiguration() {
    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Confirmation',
        text: `Are you sure you want to delete Route Configuration '${this.wizard.configurationName}'?`,
      },
    });

    firstValueFrom(dialog.afterClosed()).then(result => {
      if (result) {
        this.wizard.deleteRouteConfiguration().then(() => {
          this.snackBar.open('Route Configuration has been deleted', null, { duration: 2000 });
          this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_ROUTES]).then();
        });
      }
    });
  }
}
